import React, { useState, useEffect, useCallback, useRef } from "react";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc, setDoc, getDoc, updateDoc, collection, query, getDocs, deleteDoc, orderBy,
} from "firebase/firestore";
import "./App.css";

// ─── Constants ───────────────────────────────────────────────────────────────
const MILESTONES = [1, 3, 7, 14, 21, 30, 50, 100];
const DURATION_DAYS = 7; // Testing mode — change to 21 for production

const ENCOURAGEMENTS = [
  "That's how it's done.",
  "Your word means something.",
  "One more rep for the right muscle.",
  "Consistency is power.",
  "The compound effect in action.",
  "Building in the right direction.",
  "The muscle remembers this.",
];

const L_DATA = {
  loose: {
    label: "Loose", desc: "I overcommitted. Too big for right now.",
    conversion: "tighten",
    message: "Size it smaller. Go back to a version you can keep on your worst day.",
    step: "Step 3 — Can I actually keep this? (Ch 8)",
  },
  lie: {
    label: "Lie", desc: "I said I would and didn't.",
    conversion: "own",
    message: "What got in the way? Name it honestly. Prepare your counter-response so you're ready next time.",
    step: "Steps 4–5 — What will try to stop me? (Ch 8)",
  },
  lost: {
    label: "Lost", desc: "I forgot why it mattered.",
    conversion: "learn",
    message: "Reconnect to your why. If it no longer matters, release it deliberately — that's strength, not failure.",
    step: "Step 1 — Why does this matter? (Ch 8) · Conscious Release (Ch 7)",
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function dateStr(d) {
  if (!d) d = new Date();
  if (typeof d === "string") return d;
  return d.toISOString().split("T")[0];
}
function addDays(ds, n) {
  const d = new Date(ds + "T12:00:00");
  d.setDate(d.getDate() + n);
  return dateStr(d);
}
function daysBetween(a, b) {
  return Math.round((new Date(b + "T12:00:00") - new Date(a + "T12:00:00")) / 86400000);
}
function yesterday() { return addDays(dateStr(), -1); }
function formatDateShort(ds) {
  const d = new Date(ds + "T12:00:00");
  const m = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${m[d.getMonth()]} ${d.getDate()}`;
}

// ─── Insight Generator ───────────────────────────────────────────────────────
function generateInsight(logs, commitment) {
  const totalW = commitment.totalWs;
  const totalL = commitment.totalLs;
  const total = totalW + totalL;
  const daysLogged = total;
  const totalDays = daysBetween(commitment.startDate, commitment.endDate) + 1;
  const unlogged = totalDays - daysLogged;
  const rate = total > 0 ? Math.round((totalW / total) * 100) : 0;

  const sorted = [...logs].sort((a, b) => a.date.localeCompare(b.date));
  const lLogs = sorted.filter(l => l.result === "L");
  const loose = lLogs.filter(l => l.lType === "loose").length;
  const lie = lLogs.filter(l => l.lType === "lie").length;
  const lost = lLogs.filter(l => l.lType === "lost").length;

  let parts = [];

  // Promise rate bracket
  if (rate === 100) parts.push("Perfect. Every single day, your word held.");
  else if (rate >= 85) parts.push(`You kept your word ${rate}% of the time. That's a strong foundation.`);
  else if (rate >= 70) parts.push(`You kept your word ${rate}% of the time. Solid ground to build on.`);
  else if (rate >= 50) parts.push(`You kept your word ${rate}% of the time. The muscle is building — keep going.`);
  else parts.push(`You kept your word ${rate}% of the time. Every W counted. The next cycle starts stronger.`);

  // Dominant L type
  if (totalL > 0) {
    const max = Math.max(loose, lie, lost);
    if (loose === max && loose > 0) parts.push("Most of your L's were Loose — your promises might be too big for right now. Size down.");
    else if (lie === max && lie > 0) parts.push("The Trickster won more than once. Rebuild your counter-responses — Steps 4–5.");
    else if (lost === max && lost > 0) parts.push("You lost connection to the why. Reconnect to what matters — or release deliberately.");
  }

  // Streak pattern
  if (commitment.bestStreak >= totalDays * 0.6) {
    parts.push(`Your best streak was ${commitment.bestStreak} days — more than half the cycle.`);
  }

  // Bookend comparison
  if (sorted.length >= 4) {
    const firstHalf = sorted.slice(0, Math.floor(sorted.length / 2));
    const secondHalf = sorted.slice(Math.floor(sorted.length / 2));
    const firstW = firstHalf.filter(l => l.result === "W").length / firstHalf.length;
    const secondW = secondHalf.filter(l => l.result === "W").length / secondHalf.length;
    if (secondW > firstW + 0.15) parts.push("You finished stronger than you started.");
    else if (firstW > secondW + 0.15) parts.push("You started strong — the mid-cycle dip is normal. Next round, you'll be ready for it.");
  }

  // Unlogged
  if (unlogged > 0) parts.push(`${unlogged} day${unlogged > 1 ? "s" : ""} went unlogged.`);

  return parts.join(" ");
}

// ─── Firebase Helpers ────────────────────────────────────────────────────────
async function saveUserProfile(uid, data) {
  await setDoc(doc(db, "users", uid), data, { merge: true });
}
async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}
async function saveCommitment(uid, data) {
  await setDoc(doc(db, "users", uid, "commitments", "active"), data);
}
async function getCommitment(uid) {
  const snap = await getDoc(doc(db, "users", uid, "commitments", "active"));
  return snap.exists() ? snap.data() : null;
}
async function saveDailyLog(uid, dateKey, data) {
  await setDoc(doc(db, "users", uid, "commitments", "active", "dailyLogs", dateKey), data);
}
async function getAllLogs(uid) {
  const q = query(collection(db, "users", uid, "commitments", "active", "dailyLogs"), orderBy("date"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
async function archiveToFirestore(uid, commitmentData, logs) {
  const archiveId = `archived_${Date.now()}`;
  await setDoc(doc(db, "users", uid, "commitments", archiveId), { ...commitmentData, archivedAt: dateStr(), logs });
  const logSnap = await getDocs(collection(db, "users", uid, "commitments", "active", "dailyLogs"));
  for (const logDoc of logSnap.docs) await deleteDoc(logDoc.ref);
  await deleteDoc(doc(db, "users", uid, "commitments", "active"));
}
async function getArchivedCommitments(uid) {
  const q = query(collection(db, "users", uid, "commitments"));
  const snap = await getDocs(q);
  return snap.docs.filter(d => d.id !== "active" && d.data().archivedAt).map(d => ({ id: d.id, ...d.data() }));
}

// ─── Animations ──────────────────────────────────────────────────────────────
function Confetti({ active }) {
  if (!active) return null;
  const ps = Array.from({ length: 50 }, (_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 0.6,
    dur: 1.4 + Math.random() * 1, size: 4 + Math.random() * 7,
    color: ["#C8A951","#A88B3A","#E8D48B","#B8963A","#F5ECD7","#FFFFFF"][i % 6],
    drift: (Math.random() - 0.5) * 60,
  }));
  return (
    <div className="confetti-container">
      {ps.map(p => (
        <div key={p.id} className="confetti-particle" style={{
          left: `${p.left}%`, width: p.size, height: p.size * 1.5,
          backgroundColor: p.color, "--dr": `${p.drift}px`,
          animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`,
        }} />
      ))}
    </div>
  );
}

function PulseW() {
  return (
    <div className="pulse-container">
      <div className="pulse-ring pulse-ring-1" />
      <div className="pulse-ring pulse-ring-2" />
      <div className="pulse-w">W</div>
    </div>
  );
}

// ─── Shared UI ───────────────────────────────────────────────────────────────
function Shell({ children, showNav, navActive, onNav }) {
  return (
    <div className="app-shell">
      <div className="app-content" style={{ paddingBottom: showNav ? 74 : 0 }}>{children}</div>
      {showNav && (
        <div className="bottom-nav">
          {[
            { id: "dashboard", icon: "◉", label: "Today" },
            { id: "history", icon: "▦", label: "History" },
            { id: "settings", icon: "⚙", label: "Settings" },
          ].map(t => (
            <button key={t.id} onClick={() => onNav(t.id)} className={`nav-btn ${navActive === t.id ? "nav-active" : ""}`}>
              <span className="nav-icon">{t.icon}</span>
              <span className="nav-label">{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", className = "", disabled = false, style = {} }) {
  return (
    <button onClick={disabled ? undefined : onClick}
      className={`btn btn-${variant} ${className}`}
      style={{ ...style, opacity: disabled ? 0.4 : 1, cursor: disabled ? "default" : "pointer" }}>
      {children}
    </button>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div className="input-group">
      {label && <label className="input-label">{label}</label>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="input-field" />
    </div>
  );
}

function Hdr({ title, subtitle }) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

function Dots({ total, current }) {
  return (
    <div className="progress-dots">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`dot ${i === current ? "dot-active" : ""}`} />
      ))}
    </div>
  );
}

function WLButtons({ onW, onL, size = "large" }) {
  return (
    <div className="wl-buttons">
      <button onClick={onW} className={`wl-btn ${size}`}>W</button>
      <button onClick={onL} className={`wl-btn ${size}`}>L</button>
    </div>
  );
}

// ─── Detail Card Modal ───────────────────────────────────────────────────────
function DayDetailModal({ log, onClose }) {
  if (!log) return null;
  const isW = log.result === "W";
  const lData = !isW && log.lType ? L_DATA[log.lType] : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <p className="modal-date">{log.date}</p>
        {isW ? (
          <>
            <div className="detail-badge detail-badge-w">W</div>
            <h3 className="detail-title">W — Word Kept ✓</h3>
            <p className="detail-message">"I keep my word. I commit."</p>
          </>
        ) : lData ? (
          <>
            <div className="detail-badge detail-badge-l">{lData.conversion.toUpperCase()}</div>
            <h3 className="detail-title">L — {lData.label}</h3>
            <p className="detail-desc">"{lData.desc}"</p>
            <div className="detail-conversion">
              <p className="detail-conversion-label">→ {lData.conversion.charAt(0).toUpperCase() + lData.conversion.slice(1)}:</p>
              <p className="detail-conversion-text">{lData.message}</p>
            </div>
            <p className="detail-step">{lData.step}</p>
          </>
        ) : (
          <>
            <div className="detail-badge detail-badge-l">L</div>
            <h3 className="detail-title">L — Logged</h3>
          </>
        )}
        <button onClick={onClose} className="modal-close-btn">Close</button>
      </div>
    </div>
  );
}

// ─── Loading Screen ──────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <Shell>
      <div className="center-screen">
        <div className="logo-circle"><span className="logo-text">PM</span></div>
        <p className="loading-text">Loading...</p>
      </div>
    </Shell>
  );
}

// ─── Screen 1: Welcome ──────────────────────────────────────────────────────
function WelcomeScreen({ onSignup, onLogin }) {
  return (
    <Shell>
      <div className="center-screen" style={{ padding: "40px 28px" }}>
        <div className="logo-circle logo-large"><span className="logo-text logo-text-large">PM</span></div>
        <h1 className="welcome-title">The Promise Muscle</h1>
        <p className="welcome-tagline">I Keep My Word</p>
        <p className="welcome-desc">Build the most important skill you'll ever develop — one small kept promise at a time.</p>
        <div className="welcome-buttons">
          <Btn onClick={onSignup}>Get Started</Btn>
          <Btn variant="ghost" onClick={onLogin}>I already have an account</Btn>
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen 2: Auth ──────────────────────────────────────────────────────────
function AuthScreen({ mode, onComplete, onToggleMode, onBack }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      if (mode === "signup") await createUserWithEmailAndPassword(auth, email, pw);
      else await signInWithEmailAndPassword(auth, email, pw);
      onComplete();
    } catch (err) {
      const msgs = {
        "auth/email-already-in-use": "This email is already registered. Try logging in.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/invalid-email": "Please enter a valid email.",
        "auth/user-not-found": "No account found with this email.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-credential": "Invalid email or password.",
      };
      setError(msgs[err.code] || err.message);
    }
    setLoading(false);
  };

  return (
    <Shell>
      <div style={{ padding: "0 28px" }}>
        <button onClick={onBack} className="back-btn">← Back</button>
        <Hdr title={mode === "signup" ? "Create Your Account" : "Welcome Back"}
          subtitle={mode === "signup" ? "Your promise starts here." : "Log in to continue."} />
        <div style={{ marginTop: 20 }}>
          <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
          <Input label="Password" value={pw} onChange={setPw} type="password" placeholder="At least 6 characters" />
          {error && <p className="auth-error">{error}</p>}
          <div style={{ marginTop: 24 }}>
            <Btn onClick={handleSubmit} disabled={!email || pw.length < 6 || loading}>
              {loading ? "..." : mode === "signup" ? "Create Account" : "Log In"}
            </Btn>
          </div>
          <button onClick={onToggleMode} className="toggle-auth">
            {mode === "signup" ? "Already have an account? Log in" : "Need an account? Sign up"}
          </button>
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen 3: Onboarding ────────────────────────────────────────────────────
function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [checkIn, setCheckIn] = useState("08:00");

  const steps = [
    <div key="name">
      <Hdr title="What should we call you?" subtitle="This is how the app talks to you." />
      <Dots total={3} current={0} />
      <div style={{ padding: "0 28px" }}>
        <Input value={name} onChange={setName} placeholder="Your first name" />
        <div style={{ marginTop: 16 }}><Btn onClick={() => setStep(1)} disabled={!name}>Continue</Btn></div>
      </div>
    </div>,
    <div key="tz">
      <Hdr title="When does your day end?" subtitle="We'll use your timezone to track your days correctly." />
      <Dots total={3} current={1} />
      <div style={{ padding: "0 28px" }}>
        <div className="tz-box">
          <p className="tz-label">Detected timezone</p>
          <p className="tz-value">{Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"}</p>
        </div>
        <div style={{ marginTop: 24 }}><Btn onClick={() => setStep(2)}>Looks Right</Btn></div>
      </div>
    </div>,
    <div key="checkin">
      <Hdr title="When should we check in?" subtitle="If you forget to log, we'll nudge you the next morning." />
      <Dots total={3} current={2} />
      <div style={{ padding: "0 28px" }}>
        <div className="time-picker-wrap">
          <input type="time" value={checkIn} onChange={e => setCheckIn(e.target.value)} className="time-picker" />
        </div>
        <p className="checkin-hint">"Did you keep your word yesterday?" — sent only if you haven't logged.</p>
        <div style={{ marginTop: 24 }}><Btn onClick={() => onComplete({ name, checkIn })}>Let's Go</Btn></div>
      </div>
    </div>,
  ];
  return <Shell>{steps[step]}</Shell>;
}

// ─── Screen 4: New Promise ───────────────────────────────────────────────────
function NewPromiseScreen({ userName, onCommit }) {
  const [title, setTitle] = useState("");
  const isEvening = new Date().getHours() >= 18;
  const [startTomorrow, setStartTomorrow] = useState(isEvening);
  const startDate = startTomorrow ? addDays(dateStr(), 1) : dateStr();
  const endDate = addDays(startDate, DURATION_DAYS - 1);

  return (
    <Shell>
      <div style={{ padding: "0 28px" }}>
        <Hdr title={`${userName}, what's your promise?`} subtitle="Start small. So small it almost feels silly. The size doesn't matter yet. The completion does." />
        <div style={{ marginTop: 16 }}>
          <Input value={title} onChange={setTitle} placeholder="Drink a glass of water every morning" />
          <div className="start-date-buttons">
            {[{ val: false, label: "Start Today" }, { val: true, label: "Start Tomorrow" }].map(o => (
              <button key={String(o.val)} onClick={() => setStartTomorrow(o.val)}
                className={`start-date-btn ${startTomorrow === o.val ? "active" : ""}`}>{o.label}</button>
            ))}
          </div>
          <Btn onClick={() => onCommit(title, startDate, endDate)} disabled={!title}>
            I Keep My Word. I Commit.
          </Btn>
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen 4b: Promise Flash ────────────────────────────────────────────────
function PromiseFlashScreen({ title, startDate, endDate, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 4500); return () => clearTimeout(t); }, [onDone]);
  return (
    <Shell>
      <div onClick={onDone} className="center-screen flash-screen">
        <div className="flash-fade-up">
          <div className="flash-icon-circle"><span className="flash-icon">✦</span></div>
          <h1 className="flash-title">{title}</h1>
        </div>
        <div className="flash-fade-in-1">
          <p className="flash-dates">{formatDateShort(startDate)} → {formatDateShort(endDate)}</p>
          <p className="flash-duration">{DURATION_DAYS} days starts now.</p>
        </div>
        <div className="flash-fade-in-2">
          <p className="flash-declaration">I keep my word. I commit.</p>
        </div>
        <p className="flash-tap flash-fade-in-3">Tap to continue</p>
      </div>
    </Shell>
  );
}

// ─── Screen 5: Dashboard ─────────────────────────────────────────────────────
function DashboardScreen({ user, commitment, logs, onW, onL, onNav, onLogYesterday }) {
  const today = dateStr();
  const todayLog = logs.find(l => l.date === today);
  const yDate = yesterday();
  const yLog = logs.find(l => l.date === yDate);
  const needsYPrompt = !yLog && commitment.startDate <= yDate && commitment.status === "active";

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const total = commitment.totalWs + commitment.totalLs;
  const promiseRate = total > 0 ? Math.round((commitment.totalWs / total) * 100) : 0;
  const daysElapsed = Math.max(0, daysBetween(commitment.startDate, today));
  const daysRemaining = Math.max(0, daysBetween(today, commitment.endDate));
  const isLastDay = today === commitment.endDate;
  const progress = Math.min(1, isLastDay && todayLog ? 1 : daysElapsed / DURATION_DAYS);
  const dayNum = Math.min(daysElapsed + 1, DURATION_DAYS);
  const totalDays = daysBetween(commitment.startDate, commitment.endDate) + 1;
  const daysLogged = logs.length;

  return (
    <Shell showNav navActive="dashboard" onNav={onNav}>
      <div style={{ padding: "48px 24px 24px" }}>
        <p className="greeting-label">{greeting},</p>
        <h1 className="greeting-name">{user.displayName}</h1>

        {needsYPrompt && !todayLog && (
          <div className="yesterday-prompt">
            <p className="yesterday-title">Yesterday — did you keep your word?</p>
            <div style={{ marginTop: 14 }}><WLButtons onW={() => onLogYesterday("W")} onL={() => onLogYesterday("L")} size="small" /></div>
            <p className="yesterday-date">{yDate}</p>
          </div>
        )}

        <div className="commitment-card">
          <p className="card-label">Active Promise</p>
          <h2 className="card-title">{commitment.title}</h2>

          <div className="progress-section">
            <div className="progress-labels">
              <span className="progress-day">Day {dayNum} of {DURATION_DAYS}</span>
              <span className="progress-remaining">{daysRemaining > 0 ? `${daysRemaining} left` : "Last day!"}</span>
            </div>
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress * 100}%` }} /></div>
          </div>

          <div className={`streak-box ${commitment.currentStreak > 0 ? "streak-active" : ""}`}>
            <span className="streak-icon">{commitment.currentStreak > 0 ? "🔥" : "○"}</span>
            <span className="streak-text">
              {commitment.currentStreak > 0 ? `${commitment.currentStreak} day streak` : "No active streak"}
            </span>
          </div>

          <div className="stats-row">
            <div className="stat"><p className="stat-value stat-gold">{commitment.totalWs}</p><p className="stat-label">W's</p></div>
            <div className="stat"><p className="stat-value stat-rose">{commitment.totalLs}</p><p className="stat-label">L's</p></div>
            <div className="stat"><p className="stat-value">{promiseRate}%</p><p className="stat-label">Promise Rate</p></div>
            <div className="stat"><p className="stat-value">{daysLogged}/{totalDays}</p><p className="stat-label">Logged</p></div>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          {commitment.status === "none" ? null :
           !todayLog && today >= commitment.startDate ? (
            <div className="daily-action">
              <p className="daily-question">Did you keep your word today?</p>
              <div style={{ marginTop: 20 }}><WLButtons onW={onW} onL={onL} /></div>
            </div>
          ) : todayLog ? (
            <div className={`logged-today ${todayLog.result === "W" ? "logged-w" : "logged-l"}`}>
              <span>Today: <strong>{todayLog.result === "W" ? "W — Word kept ✓" : `L — ${L_DATA[todayLog.lType]?.label || "Logged"}`}</strong></span>
            </div>
          ) : (
            <div className="not-started">Your promise starts {formatDateShort(commitment.startDate)}. Almost time.</div>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen 6: W Confirmation ────────────────────────────────────────────────
function WConfirmScreen({ streak, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 5500); return () => clearTimeout(t); }, [onDone]);
  return (
    <Shell>
      <Confetti active={true} />
      <div onClick={onDone} className="center-screen" style={{ cursor: "pointer", padding: "40px 28px" }}>
        <PulseW />
        <h1 className="w-confirm-title">I did it. I keep my word.</h1>
        {MILESTONES.includes(streak) ? (
          <p className="w-milestone-msg">
            {streak === 1 ? "First W. This is where it starts." :
             streak === 3 ? "3 days. You showed up every single day." :
             streak === 7 ? "7 days. A full week of keeping your word." :
             streak === 14 ? "14 days. Two weeks strong." :
             streak === 21 ? "21 days. You have the receipts now." :
             streak === 30 ? "30 days. Your word is who you are." :
             `${streak} days. The muscle is real.`}
          </p>
        ) : (
          <p className="w-encourage">{ENCOURAGEMENTS[streak % ENCOURAGEMENTS.length]}</p>
        )}
        <div className="w-streak-badge">🔥 {streak} day{streak !== 1 ? "s" : ""}</div>
        <p className="tap-hint">Tap anywhere to continue</p>
      </div>
    </Shell>
  );
}

// ─── Screen 7: L Diagnostic ─────────────────────────────────────────────────
function LDiagScreen({ onSelect, onBack }) {
  const [sel, setSel] = useState(null);
  if (sel) {
    const data = L_DATA[sel];
    return (
      <Shell>
        <div className="center-screen" style={{ padding: "40px 28px" }}>
          <div className="detail-badge detail-badge-l">{data.conversion.toUpperCase()}</div>
          <h2 className="l-result-title">That's a {data.label}.</h2>
          <p className="l-result-message">{data.message}</p>
          <p className="detail-step" style={{ marginTop: 12 }}>{data.step}</p>
          <div style={{ marginTop: 30, width: "100%", maxWidth: 300 }}><Btn onClick={() => onSelect(sel)}>Got It</Btn></div>
          <p className="l-reminder">That's data, not failure.</p>
        </div>
      </Shell>
    );
  }
  return (
    <Shell>
      <div style={{ padding: "0 28px" }}>
        <button onClick={onBack} className="back-btn">← Back</button>
        <Hdr title="What happened?" subtitle="No judgment. Just awareness." />
        <div className="l-options">
          {Object.entries(L_DATA).map(([key, data]) => (
            <button key={key} onClick={() => setSel(key)} className="l-option-btn">
              <div className="l-option-header">
                <p className="l-option-label">{data.label}</p>
                <span className="chevron">›</span>
              </div>
              <p className="l-option-desc">{data.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen 8: Summary ───────────────────────────────────────────────────────
function SummaryScreen({ commitment, logs, userName, onKeepGoing, onStartNew, onDone }) {
  const [expandedL, setExpandedL] = useState(null);

  const total = commitment.totalWs + commitment.totalLs;
  const rate = total > 0 ? Math.round((commitment.totalWs / total) * 100) : 0;
  const totalDays = daysBetween(commitment.startDate, commitment.endDate) + 1;
  const daysLogged = logs.length;
  const unlogged = totalDays - daysLogged;
  const sorted = [...logs].sort((a, b) => a.date.localeCompare(b.date));
  const lLogs = sorted.filter(l => l.result === "L");
  const loose = lLogs.filter(l => l.lType === "loose").length;
  const lie = lLogs.filter(l => l.lType === "lie").length;
  const lost = lLogs.filter(l => l.lType === "lost").length;

  // Build day grid
  const dayGrid = [];
  for (let i = 0; i < totalDays; i++) {
    const d = addDays(commitment.startDate, i);
    const log = logs.find(l => l.date === d);
    dayGrid.push({ day: i + 1, date: d, result: log ? log.result : null, lType: log?.lType });
  }

  const insight = generateInsight(logs, commitment);
  const rowSize = DURATION_DAYS <= 7 ? DURATION_DAYS : 7;
  const rows = [];
  for (let i = 0; i < dayGrid.length; i += rowSize) {
    rows.push(dayGrid.slice(i, i + rowSize));
  }

  return (
    <Shell>
      <Confetti active={true} />
      <div style={{ padding: "48px 28px 40px", textAlign: "center" }}>
        <p className="summary-header">{DURATION_DAYS} DAYS COMPLETE</p>
        <h1 className="summary-title">{userName}, here's your record.</h1>
        <p className="summary-subtitle">
          {DURATION_DAYS === 21 ? "This isn't a report card. It's evidence of who you've been for three weeks." :
           `This isn't a report card. It's evidence of who you've been for ${DURATION_DAYS} days.`}
        </p>

        <div className="summary-card">
          <div className="summary-stats-row">
            <div className="summary-stat"><p className="summary-stat-value stat-gold">{commitment.totalWs}</p><p className="summary-stat-label">W's</p></div>
            <div className="summary-stat"><p className="summary-stat-value stat-rose">{commitment.totalLs}</p><p className="summary-stat-label">L's</p></div>
            <div className="summary-stat"><p className="summary-stat-value">{rate}%</p><p className="summary-stat-label">Promise Rate</p></div>
            <div className="summary-stat"><p className="summary-stat-value">{daysLogged}/{totalDays}</p><p className="summary-stat-label">Logged</p></div>
          </div>

          <div className="summary-details">
            <p className="summary-streak">Best streak: {commitment.bestStreak} days</p>
            {commitment.totalLs > 0 && (
              <div>
                <p className="summary-l-header">L breakdown:</p>
                <div className="summary-l-breakdown">
                  {[{ l: "Loose", n: loose }, { l: "Lie", n: lie }, { l: "Lost", n: lost }].filter(x => x.n > 0).map(x => (
                    <span key={x.l} className="summary-l-type">{x.l}: {x.n}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Day Grid */}
          <div className="summary-grid-section">
            <p className="summary-grid-label">Your {DURATION_DAYS} days:</p>
            <div className="summary-grid-rows">
              {rows.map((row, ri) => (
                <div key={ri} className="summary-grid-row">
                  {DURATION_DAYS > 7 && <span className="summary-wk-label">Wk {ri + 1}</span>}
                  <div className="summary-grid-cells">
                    {row.map((d, i) => (
                      <div key={i} className={`summary-grid-cell ${d.result === "W" ? "cell-w" : d.result === "L" ? "cell-l" : "cell-blank"}`}>
                        {d.result || "·"}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Insight */}
        {insight && (
          <div className="insight-box">
            <p className="insight-text">{insight}</p>
          </div>
        )}

        {/* L Cards */}
        {lLogs.length > 0 && (
          <div className="summary-l-cards">
            <p className="section-label-left">Your L's</p>
            {lLogs.map((l, i) => {
              const lInfo = L_DATA[l.lType];
              const dayNum = daysBetween(commitment.startDate, l.date) + 1;
              const isExpanded = expandedL === i;
              return (
                <div key={i} className="summary-l-card" onClick={() => setExpandedL(isExpanded ? null : i)}>
                  <div className="summary-l-card-header">
                    <span className="summary-l-card-day">Day {dayNum}</span>
                    <span className="summary-l-card-type">{lInfo?.label || "L"}</span>
                    <span className="chevron">{isExpanded ? "⌄" : "›"}</span>
                  </div>
                  {isExpanded && lInfo && (
                    <div className="summary-l-card-detail">
                      <p className="summary-l-card-desc">"{lInfo.desc}"</p>
                      <p className="summary-l-card-conv"><strong>→ {lInfo.conversion.charAt(0).toUpperCase() + lInfo.conversion.slice(1)}:</strong> {lInfo.message}</p>
                      <p className="summary-l-card-step">{lInfo.step}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Blank day options */}
        {unlogged > 0 && (
          <div className="blank-day-options">
            <p className="blank-day-label">{unlogged} day{unlogged > 1 ? "s" : ""} unlogged</p>
            <p className="blank-day-hint">Promise Rate reflects only the days you logged. You can leave it or go back and fill them in.</p>
          </div>
        )}

        <p className="summary-message">
          {rate >= 80 ? "You kept your word. The W's are the proof." :
           rate > 0 ? "The W's show what you're capable of. The L's show what needs attention. Both are useful." :
           "Every cycle teaches you something. The next one starts now."}
        </p>

        <div className="summary-actions">
          <Btn onClick={onKeepGoing}>Keep Going — Another {DURATION_DAYS} Days</Btn>
          <Btn variant="secondary" onClick={onStartNew}>Start a New Promise</Btn>
          <Btn variant="ghost" onClick={onDone}>I'm Done for Now</Btn>
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen 9: History ───────────────────────────────────────────────────────
function HistoryScreen({ commitment, logs, archived, onNav, onBackfillW, onBackfillL }) {
  const [vm, setVm] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() } });
  const [selBlank, setSelBlank] = useState(null);
  const [detailLog, setDetailLog] = useState(null);

  const mNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dim = new Date(vm.y, vm.m + 1, 0).getDate();
  const fdow = new Date(vm.y, vm.m, 1).getDay();
  const logMap = {}; logs.forEach(l => { logMap[l.date] = l });
  const today = dateStr();

  const cells = [];
  for (let i = 0; i < fdow; i++) cells.push(<div key={`e${i}`} />);
  for (let d = 1; d <= dim; d++) {
    const ds = `${vm.y}-${String(vm.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const log = logMap[ds];
    const isToday = ds === today;
    const inComm = commitment.status === "active" && ds >= commitment.startDate && ds <= commitment.endDate;
    const isBlank = !log && inComm && ds <= today;
    const isLogged = !!log;
    cells.push(
      <div key={d}
        onClick={isBlank ? () => setSelBlank(ds) : isLogged ? () => setDetailLog(log) : undefined}
        className={`cal-cell ${log ? (log.result === "W" ? "cal-w" : "cal-l") : ""} ${isToday && !log ? "cal-today" : ""} ${isBlank ? "cal-blank" : ""} ${isLogged ? "cal-tappable" : ""}`}>
        {d}
      </div>
    );
  }

  const total = commitment.totalWs + commitment.totalLs;
  const rate = total > 0 ? Math.round((commitment.totalWs / total) * 100) : 0;
  const totalDays = commitment.startDate ? daysBetween(commitment.startDate, commitment.endDate) + 1 : 0;
  const daysLogged = logs.length;
  const chM = delta => setVm(p => { let m = p.m + delta, y = p.y; if (m < 0) { m = 11; y-- } if (m > 11) { m = 0; y++ } return { y, m } });

  return (
    <Shell showNav navActive="history" onNav={onNav}>
      <div style={{ padding: "48px 24px 24px" }}>
        <h1 className="screen-title">History</h1>

        {commitment.status === "active" && (
          <>
            <div className="history-stats">
              {[
                { l: "W's", v: commitment.totalWs, c: "stat-gold" },
                { l: "L's", v: commitment.totalLs, c: "stat-rose" },
                { l: "Promise Rate", v: `${rate}%`, c: "" },
                { l: "Logged", v: `${daysLogged}/${totalDays}`, c: "" },
              ].map(s => (
                <div key={s.l} className="stat">
                  <p className={`stat-value ${s.c}`}>{s.v}</p>
                  <p className="stat-label">{s.l}</p>
                </div>
              ))}
            </div>

            <div className="month-nav">
              <button onClick={() => chM(-1)} className="month-btn">‹</button>
              <span className="month-label">{mNames[vm.m]} {vm.y}</span>
              <button onClick={() => chM(1)} className="month-btn">›</button>
            </div>

            <div className="cal-header">
              {["S","M","T","W","T","F","S"].map((d, i) => (<div key={i} className="cal-day-label">{d}</div>))}
            </div>
            <div className="cal-grid">{cells}</div>

            <div className="cal-legend">
              <div className="legend-item"><div className="legend-swatch legend-w" /><span>W</span></div>
              <div className="legend-item"><div className="legend-swatch legend-l" /><span>L</span></div>
              <div className="legend-item"><div className="legend-swatch legend-blank" /><span>Tap to log</span></div>
            </div>
          </>
        )}

        {commitment.status === "none" && archived.length === 0 && (
          <p className="empty-history">No promises yet.</p>
        )}

        {/* Backfill modal */}
        {selBlank && (
          <div className="modal-overlay" onClick={() => setSelBlank(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <p className="modal-date">{selBlank}</p>
              <p className="modal-question">Did you keep your word?</p>
              <div style={{ marginTop: 20 }}>
                <WLButtons onW={() => { onBackfillW(selBlank); setSelBlank(null) }} onL={() => { onBackfillL(selBlank); setSelBlank(null) }} />
              </div>
              <button onClick={() => setSelBlank(null)} className="modal-cancel">Cancel</button>
            </div>
          </div>
        )}

        {/* Day detail modal */}
        <DayDetailModal log={detailLog} onClose={() => setDetailLog(null)} />

        {/* Archived */}
        {archived.length > 0 && (
          <div className="archived-section">
            <p className="section-label">Past Promises</p>
            {archived.map((ac, i) => {
              const at = ac.totalWs + ac.totalLs;
              const ar = at > 0 ? Math.round((ac.totalWs / at) * 100) : 0;
              return (
                <div key={i} className="archived-card">
                  <p className="archived-title">{ac.title}</p>
                  <p className="archived-dates">{formatDateShort(ac.startDate)} → {formatDateShort(ac.endDate)} · {ac.status === "released" ? "Released" : "Completed"}</p>
                  <div className="archived-stats">
                    <span className="stat-gold">{ac.totalWs} W's</span>
                    <span className="stat-rose">{ac.totalLs} L's</span>
                    <span>{ar}% Promise Rate</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}

// ─── Screen 10: Settings ─────────────────────────────────────────────────────
function SettingsScreen({ user, commitment, onNav, onRelease, onSignOut }) {
  const [showConfirm, setShowConfirm] = useState(false);

  const Section = ({ title, children }) => (
    <div className="settings-section">
      <p className="section-label">{title}</p>
      <div className="settings-group">{children}</div>
    </div>
  );
  const Row = ({ label, value, onClick, danger = false }) => (
    <button onClick={onClick} className={`settings-row ${danger ? "danger" : ""} ${onClick ? "clickable" : ""}`}>
      <span>{label}</span>
      {value && <span className="row-value">{value}</span>}
      {onClick && !value && <span className="chevron">›</span>}
    </button>
  );

  return (
    <Shell showNav navActive="settings" onNav={onNav}>
      <div style={{ padding: "48px 24px 24px" }}>
        <h1 className="screen-title" style={{ marginBottom: 24 }}>Settings</h1>
        <Section title="Profile">
          <Row label="Name" value={user.displayName} />
          <Row label="Email" value={user.email} />
        </Section>
        <Section title="Preferences">
          <Row label="Check-in Time" value={user.checkInTime || "8:00 AM"} />
          <Row label="Notifications" value="On" />
          <Row label="Timezone" value={Intl.DateTimeFormat().resolvedOptions().timeZone?.split("/")[1] || "New York"} />
        </Section>
        {commitment.status === "active" && (
          <Section title="Promise">
            <Row label={commitment.title} value={`${commitment.totalWs}W / ${commitment.totalLs}L`} />
            <Row label="Release this promise" onClick={() => setShowConfirm(true)} danger />
          </Section>
        )}
        <Section title="Account">
          <Row label="Sign Out" onClick={onSignOut} />
        </Section>
        <div className="settings-footer">
          <p>The Promise Muscle — v2.0</p>
          <p>{DURATION_DAYS}-day {DURATION_DAYS === 21 ? "" : "test "}mode</p>
        </div>
      </div>

      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Release this promise?</h3>
            <p className="modal-commitment">"{commitment.title}"</p>
            <p className="modal-desc">Your logged days will be archived. This is deliberate, not quitting — Chapter 7 style.</p>
            <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
              <Btn onClick={() => { setShowConfirm(false); onRelease() }} style={{ backgroundColor: "#C44" }}>Release and Archive</Btn>
              <Btn variant="ghost" onClick={() => setShowConfirm(false)}>Keep My Promise</Btn>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

// ─── App Controller ──────────────────────────────────────────────────────────
const EMPTY = { title: "", status: "none", startDate: "", endDate: "", currentStreak: 0, bestStreak: 0, totalWs: 0, totalLs: 0 };

export default function App() {
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [screen, setScreen] = useState("welcome");
  const [authMode, setAuthMode] = useState("signup");
  const [user, setUser] = useState({ displayName: "", email: "", checkInTime: "08:00" });
  const [commitment, setCommitment] = useState({ ...EMPTY });
  const [logs, setLogs] = useState([]);
  const [archived, setArchived] = useState([]);
  const [wData, setWData] = useState(null);
  const [pendingLDate, setPendingLDate] = useState(null);
  const [flashData, setFlashData] = useState(null);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        const profile = await getUserProfile(fbUser.uid);
        if (profile) {
          setUser({ displayName: profile.displayName, email: fbUser.email, checkInTime: profile.checkInTime || "08:00" });
          const comm = await getCommitment(fbUser.uid);
          if (comm) {
            setCommitment(comm);
            const allLogs = await getAllLogs(fbUser.uid);
            setLogs(allLogs);
            // Check if cycle is complete
            const elapsed = daysBetween(comm.startDate, dateStr());
            const lastDayLog = allLogs.find(l => l.date === comm.endDate);
            if (elapsed >= DURATION_DAYS || (dateStr() >= comm.endDate && lastDayLog)) {
              setScreen("summary");
            } else {
              setScreen("dashboard");
            }
          } else {
            setScreen("newPromise");
          }
          const arch = await getArchivedCommitments(fbUser.uid);
          setArchived(arch);
        } else {
          setScreen("onboarding");
        }
      } else {
        setScreen("welcome");
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const recalcStreaks = useCallback((newLogs, comm) => {
    if (!comm.startDate) return { currentStreak: 0, bestStreak: 0 };
    const sorted = [...newLogs].sort((a, b) => a.date.localeCompare(b.date));
    const today = dateStr();
    const checkFrom = today > comm.endDate ? comm.endDate : today;
    let current = 0;
    for (let i = 0; i < DURATION_DAYS + 30; i++) {
      const d = addDays(checkFrom, -i);
      if (d < comm.startDate) break;
      if (sorted.find(l => l.date === d && l.result === "W")) current++;
      else break;
    }
    let best = 0, streak = 0;
    sorted.forEach(l => {
      if (l.result === "W") { streak++; best = Math.max(best, streak) } else streak = 0;
    });
    return { currentStreak: current, bestStreak: Math.max(best, current) };
  }, []);

  const logEntry = useCallback(async (date, result, lType = null) => {
    if (!firebaseUser) return;
    const entry = {
      date, result, loggedAt: new Date().toISOString(), backfilled: date !== dateStr(),
      ...(lType ? { lType, lConversion: L_DATA[lType].conversion } : {}),
    };
    await saveDailyLog(firebaseUser.uid, date, entry);
    const newLogs = [...logs.filter(l => l.date !== date), entry];
    const ws = newLogs.filter(l => l.result === "W").length;
    const ls = newLogs.filter(l => l.result === "L").length;
    const streaks = recalcStreaks(newLogs, commitment);
    const updated = { ...commitment, totalWs: ws, totalLs: ls, ...streaks };
    await saveCommitment(firebaseUser.uid, updated);
    setCommitment(updated);
    setLogs(newLogs);
    return { updated, newLogs };
  }, [firebaseUser, logs, commitment, recalcStreaks]);

  const calcStreak = useCallback((forDate, currentLogs) => {
    const allLogs = [...currentLogs.filter(l => l.date !== forDate), { date: forDate, result: "W" }];
    const today = dateStr();
    let s = 0;
    for (let i = 0; i < 60; i++) {
      if (allLogs.find(l => l.date === addDays(today, -i) && l.result === "W")) s++;
      else break;
    }
    return Math.max(s, 1);
  }, []);

  const isLastDay = useCallback((comm) => {
    return dateStr() >= comm.endDate;
  }, []);

  const handleW = useCallback(async (forDate) => {
    const d = forDate || dateStr();
    const newStreak = calcStreak(d, logs);
    setWData({ streak: newStreak });
    const result = await logEntry(d, "W");
    // After W confirmation, check if cycle is complete
    if (result && d === result.updated.endDate) {
      setScreen("wConfirmThenSummary");
    } else {
      setScreen("wConfirm");
    }
  }, [logEntry, calcStreak, logs]);

  const handleL = useCallback((forDate) => {
    setPendingLDate(forDate || dateStr());
    setScreen("lDiag");
  }, []);

  const handleLSelect = useCallback(async (lType) => {
    const d = pendingLDate || dateStr();
    const result = await logEntry(d, "L", lType);
    setPendingLDate(null);
    if (result && d === result.updated.endDate) {
      setScreen("summary");
    } else {
      setScreen("dashboard");
    }
  }, [pendingLDate, logEntry]);

  const handleArchive = useCallback(async (status) => {
    if (!firebaseUser) return;
    await archiveToFirestore(firebaseUser.uid, { ...commitment, status }, logs);
    const arch = await getArchivedCommitments(firebaseUser.uid);
    setArchived(arch);
    setLogs([]);
    setCommitment({ ...EMPTY });
  }, [firebaseUser, commitment, logs]);

  const handleRelease = useCallback(async () => {
    await handleArchive("released");
    setScreen("newPromise");
  }, [handleArchive]);

  const handleSignOut = useCallback(async () => {
    await signOut(auth);
    setUser({ displayName: "", email: "", checkInTime: "08:00" });
    setCommitment({ ...EMPTY });
    setLogs([]);
    setArchived([]);
    setScreen("welcome");
  }, []);

  const handleNav = useCallback((tab) => {
    if (commitment.status === "none" && tab === "dashboard") setScreen("newPromise");
    else setScreen(tab);
  }, [commitment.status]);

  if (loading) return <LoadingScreen />;

  switch (screen) {
    case "welcome":
      return <WelcomeScreen onSignup={() => { setAuthMode("signup"); setScreen("auth") }} onLogin={() => { setAuthMode("login"); setScreen("auth") }} />;
    case "auth":
      return <AuthScreen mode={authMode} onComplete={() => {}} onToggleMode={() => setAuthMode(m => m === "signup" ? "login" : "signup")} onBack={() => setScreen("welcome")} />;
    case "onboarding":
      return <OnboardingScreen onComplete={async (data) => {
        if (firebaseUser) {
          await saveUserProfile(firebaseUser.uid, {
            displayName: data.name, email: firebaseUser.email, checkInTime: data.checkIn,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            createdAt: new Date().toISOString(), onboardingComplete: true,
            totalWs: 0, totalLs: 0, longestStreak: 0,
          });
          setUser({ displayName: data.name, email: firebaseUser.email, checkInTime: data.checkIn });
          setScreen("newPromise");
        }
      }} />;
    case "newPromise":
      return <NewPromiseScreen userName={user.displayName} onCommit={(title, start, end) => {
        setFlashData({ title, start, end }); setScreen("promiseFlash");
      }} />;
    case "promiseFlash":
      return <PromiseFlashScreen title={flashData.title} startDate={flashData.start} endDate={flashData.end}
        onDone={async () => {
          const newComm = { title: flashData.title, status: "active", startDate: flashData.start, endDate: flashData.end, currentStreak: 0, bestStreak: 0, totalWs: 0, totalLs: 0 };
          if (firebaseUser) await saveCommitment(firebaseUser.uid, newComm);
          setCommitment(newComm); setLogs([]); setFlashData(null); setScreen("dashboard");
        }} />;
    case "dashboard":
      return <DashboardScreen user={user} commitment={commitment} logs={logs}
        onW={() => handleW()} onL={() => handleL()}
        onLogYesterday={r => r === "W" ? handleW(yesterday()) : handleL(yesterday())}
        onNav={handleNav} />;
    case "wConfirm":
      return <WConfirmScreen streak={wData?.streak || 1} onDone={() => setScreen("dashboard")} />;
    case "wConfirmThenSummary":
      return <WConfirmScreen streak={wData?.streak || 1} onDone={() => setScreen("summary")} />;
    case "lDiag":
      return <LDiagScreen onSelect={handleLSelect} onBack={() => { setPendingLDate(null); setScreen("dashboard") }} />;
    case "summary":
      return <SummaryScreen commitment={commitment} logs={logs} userName={user.displayName}
        onKeepGoing={async () => {
          const updated = { ...commitment, endDate: addDays(commitment.endDate, DURATION_DAYS) };
          if (firebaseUser) await saveCommitment(firebaseUser.uid, updated);
          setCommitment(updated); setScreen("dashboard");
        }}
        onStartNew={async () => { await handleArchive("completed"); setScreen("newPromise") }}
        onDone={async () => { await handleArchive("completed"); setScreen("dashboard") }} />;
    case "history":
      return <HistoryScreen commitment={commitment} logs={logs} archived={archived}
        onNav={handleNav} onBackfillW={d => handleW(d)} onBackfillL={d => handleL(d)} />;
    case "settings":
      return <SettingsScreen user={user} commitment={commitment}
        onNav={handleNav} onRelease={handleRelease} onSignOut={handleSignOut} />;
    default:
      return <WelcomeScreen onSignup={() => { setAuthMode("signup"); setScreen("auth") }} onLogin={() => { setAuthMode("login"); setScreen("auth") }} />;
  }
}